import { ReactNode } from 'react';
import { LinkSent } from '../LinkSent/LinkSent';
import { LINK_SENT_TAIL } from '../MagicLinkCopy/linkSentCopy';
import { MagicLinkConfirmationPromptMessage } from './common/LoginAlert';
import { LoginMessageContainer } from './common/LoginContainer';
import { LoginStatusMessageDisplay } from './common/LoginStatusMessageDisplay';

export type SignInLinkRequestedResendProps = {
  /** The address the sign-in link was most recently sent (or resent) to. Required alongside
   *  `expiresAt`: LinkSent renders both unconditionally once `resend` is supplied - see LinkSent's
   *  own doc comment for why this component never derives either itself. */
  email: string;
  /** When the sign-in link this screen reports on stops working. The caller (cognito-passwordless
   *  auth's own PasswordlessLogin container) derives this via `computeLinkExpiry` from the moment
   *  it actually sent - or resent - the link, never from "now" at render time. See linkExpiry.ts's
   *  own doc comment, and GoogleSsoSignUpView.tsx for the identical pattern on the other route
   *  that derives it this way. */
  expiresAt: Date | string;
  /** Fills LinkSent's own `resend` slot: the caller-composed resend affordance (in practice
   *  LinkSentSignInResend, wired to the auth proxy's fresh-code resend per ADR-0092). This
   *  component never builds it (ADR-0050) - it only places what the caller supplies, the same
   *  rendered-ReactNode-slot pattern as GoogleSsoSignUpForm's `resendAction` and RecoverInvitation's
   *  own equivalent.
   *
   *  The one-time code itself never passes through this component or through LinkSent: whatever
   *  the caller composed here owns it entirely, from the keystroke that types it to the press that
   *  submits it, and this component never reads, stores or forwards it. A one-time code is a live
   *  possession check - see LinkSentSignInResend's own doc comment for the full protocol, and
   *  cognito-passwordless-auth's PasswordlessLogin.tsx's own `requestedSignInEmailRef` comment for
   *  why the email is the only thing about a resend that layer retains either. Nothing introduced
   *  by this rewrite adds a ref, a default, or any cross-render cache that would weaken that. */
  resend: ReactNode;
  'data-testid'?: string;
};

export type SignInLinkRequestedProps = {
  /** Opt-in: the full "Email sent!" screen (LinkSent, wired to the sign-in resend composition).
   *  Omitted (the default), this component renders exactly as it always has - a static status
   *  message with no address, no expiry and no resend affordance - so an existing caller (e.g.
   *  the isInertMagicLinkEnabled toggle off) sees no change. */
  resend?: SignInLinkRequestedResendProps;
};

export default function SignInLinkRequested({ resend }: Readonly<SignInLinkRequestedProps>) {
  if (!resend) {
    return (
      <>
        <LoginMessageContainer>
          <LoginStatusMessageDisplay>Checking your sign-in status...</LoginStatusMessageDisplay>
        </LoginMessageContainer>
        <MagicLinkConfirmationPromptMessage />
      </>
    );
  }

  // Task 5 ("Link sent, one screen", settled 2026-09-17): the spinner, generic alert and bespoke
  // resend affordance this branch drew previously (board id "2b", now retired along with 2a) are
  // gone entirely, replaced by the same shared LinkSent shell every "we sent you a magic link"
  // screen composes. Every visible string below is approved copy - matching GoogleSsoSignUpForm.tsx
  // and RecoverInvitation.tsx, the other two components that render LinkSent directly.
  return (
    <LinkSent
      heading="Email sent!"
      leadLine="We've sent a link to"
      email={resend.email}
      expiryLabel="The email link will be valid until"
      expiresAt={resend.expiresAt}
      tail={LINK_SENT_TAIL}
      resend={resend.resend}
      data-testid={resend['data-testid']}
    />
  );
}
